import { useCallback, useEffect, useRef, useState } from 'react';
import { EXPLAINERS, type Explainer } from '../lib/explainers';

interface Props {
  /** Key into EXPLAINERS. */
  id: keyof typeof EXPLAINERS;
  /** Overrides the default "What is this?" tooltip. */
  label?: string;
  /**
   * 'icon' is the "?" bubble beside a card heading. 'link' renders `children`
   * as inline text — for explainers that deserve a visible invitation rather
   * than a symbol you have to notice.
   */
  variant?: 'icon' | 'link';
  children?: React.ReactNode;
}

/**
 * The "?" affordance next to a card heading, plus its panel.
 *
 * Uses the native <dialog> element rather than a hand-rolled overlay: it gives
 * us focus trapping, Escape-to-close, inertness of the page behind, and correct
 * screen-reader semantics for free — all things a div-with-a-z-index gets wrong.
 */
export function Explain({ id, label = 'What is this?', variant = 'icon', children }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const explainer: Explainer | undefined = EXPLAINERS[id];

  const close = useCallback(() => {
    ref.current?.close();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  // Keep React state in step when the browser closes the dialog itself (Esc).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onClose = () => setOpen(false);
    el.addEventListener('close', onClose);
    return () => el.removeEventListener('close', onClose);
  }, []);

  if (!explainer) return null;

  return (
    <>
      <button
        type="button"
        className={variant === 'link' ? 'explain-link' : 'explain-btn'}
        onClick={() => setOpen(true)}
        title={label}
        aria-label={`${label} — ${explainer.title}`}
      >
        {variant === 'link' ? children : '?'}
      </button>

      <dialog
        ref={ref}
        className="explain-dialog"
        aria-labelledby={`explain-title-${id}`}
        // Clicking the backdrop (the dialog's own box is inset) dismisses.
        onClick={(e) => {
          if (e.target === ref.current) close();
        }}
      >
        <article className="explain">
          <header className="explain-head">
            <div>
              <p className="explain-kicker">What is this?</p>
              <h3 id={`explain-title-${id}`}>{explainer.title}</h3>
            </div>
            <button
              type="button"
              className="ghost icon-btn explain-close"
              onClick={close}
              aria-label="Close"
            >
              ✕
            </button>
          </header>

          <p className="explain-hook">{explainer.hook}</p>

          {explainer.sections.map((section) => (
            <section key={section.heading} className="explain-section">
              <h4>{section.heading}</h4>
              {section.paras.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              {section.facts && (
                <dl className="explain-facts">
                  {section.facts.map(([term, def]) => (
                    <div key={term}>
                      <dt>{term}</dt>
                      <dd>{def}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>
          ))}

          {explainer.reading && (
            <section className="explain-section">
              <h4>Reading the numbers on screen</h4>
              <ul className="explain-list">
                {explainer.reading.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </section>
          )}

          {explainer.limits && (
            <section className="explain-section explain-limits">
              <h4>What this can’t tell you</h4>
              <ul className="explain-list">
                {explainer.limits.map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            </section>
          )}
        </article>
      </dialog>
    </>
  );
}
